import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  Heading,
  HStack,
  Input,
  List,
  ListItem,
  Progress,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Textarea,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import axios from 'axios';
import { ENDPOINTS } from 'utils/Constants';
import { currentUser } from 'utils/CurrentUser';
import { notify } from 'components/ToastNotification';
import { useNavigate } from 'react-router-dom';
import { useI18n } from 'i18n';

const goals = ['Interview readiness', 'Build confidence', 'Apply at work', 'Exam prep'];
const levels = ['beginner', 'intermediate', 'advanced'];
const apiLanguageByUi = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  zh: 'Chinese',
  ar: 'Arabic',
  ru: 'Russian',
  ja: 'Japanese',
  hi: 'Hindi',
  sq: 'Albanian',
};

export default function PersonalCourses() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sourceType, setSourceType] = useState('topic');
  const [topic, setTopic] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [goal, setGoal] = useState(goals[0]);
  const [level, setLevel] = useState(levels[0]);
  const [minutesPerDay, setMinutesPerDay] = useState(20);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [course, setCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [expandedModuleId, setExpandedModuleId] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('#F3F7FF', '#0B1220');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const accentBg = useColorModeValue('rgba(255,255,255,0.76)', 'rgba(15,23,42,0.74)');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const heroBg = useColorModeValue(
    'linear-gradient(120deg, #0EA5E9 0%, #2563EB 50%, #1E3A8A 100%)',
    'linear-gradient(120deg, #0284C7 0%, #1D4ED8 45%, #0F172A 100%)'
  );
  const moduleBg = useColorModeValue('white', 'rgba(30,41,59,0.9)');
  const moduleBorder = useColorModeValue('gray.100', 'whiteAlpha.200');
  const premiumCardBg = useColorModeValue('orange.50', 'orange.900');
  const premiumBorder = useColorModeValue('orange.200', 'orange.500');

  const modules = useMemo(() => course?.coursePack?.modules || [], [course]);
  const totalLessons = useMemo(() => modules.reduce((sum, module) => sum + (module.lessons?.length || 0), 0), [modules]);
  const todaysPlanItems = useMemo(() => course?.coursePack?.todaysPlan || [], [course]);
  const completedLessons = useMemo(() => {
    if (!course) return 0;
    if (typeof course.completedLessons === 'number') {
      return Math.min(Math.max(course.completedLessons, 0), totalLessons);
    }
    if (typeof course.currentModuleIndex === 'number' && typeof course.currentLessonIndex === 'number') {
      return Math.min(Math.max(course.currentModuleIndex + course.currentLessonIndex, 0), totalLessons);
    }
    return 0;
  }, [course, totalLessons]);
  const progressValue = useMemo(() => {
    if (course && typeof course.progressPercentage === 'number') {
      return Math.min(Math.max(course.progressPercentage, 0), 100);
    }
    if (!totalLessons) return 0;
    return Math.round((completedLessons / totalLessons) * 100);
  }, [course, completedLessons, totalLessons]);
  const currentGenerationMode = useMemo(() => {
    const history = course?.generationHistory;
    if (!Array.isArray(history) || history.length === 0) return 'ai';
    return history[history.length - 1]?.generationMode || 'ai';
  }, [course]);

  const startNewCourse = () => {
    setStep(1);
    setCourse(null);
    setSourceType('topic');
    setTopic('');
    setSourceText('');
    setGoal(goals[0]);
    setLevel(levels[0]);
    setMinutesPerDay(20);
    setExpandedModuleId(null);
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await axios.get(ENDPOINTS.GET_PERSONAL_COURSES, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });

      if (!response?.data?.success) {
        return;
      }

      const fetchedCourses = response.data.data || [];
      setCourses(fetchedCourses);

      if (!course) {
        const activeCourse = fetchedCourses.find((item) => item.status === 'active') || fetchedCourses[0];
        if (activeCourse) {
          setCourse(activeCourse);
          setStep(3);
        }
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        setUpgradeRequired(true);
      } else {
        notify(error?.response?.data?.output || 'Could not fetch personal courses', 'error');
      }
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCourse = async () => {
    if (sourceType === 'topic' && topic.trim().length < 3) {
      notify('Please enter a topic with at least 3 characters.', 'error');
      return;
    }
    if (sourceType === 'paste' && sourceText.trim().length < 50) {
      notify('Please paste at least 50 characters so AI can build a plan.', 'error');
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        sourceType,
        topic: sourceType === 'topic' ? topic : '',
        sourceText: sourceType === 'paste' ? sourceText : '',
        goal,
        level,
        minutesPerDay,
        language: apiLanguageByUi[language] || 'English',
      };
      const response = await axios.post(ENDPOINTS.CREATE_PERSONAL_COURSE, payload, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });

      if (!response?.data?.success) {
        notify(response?.data?.output || 'Failed to generate personal course', 'error');
        return;
      }

      setCourse(response.data.data.course);
      setCourses((previous) => [response.data.data.course, ...previous.filter((item) => item.id !== response.data.data.course.id)]);
      setStep(3);
      setUpgradeRequired(false);
      if (response?.data?.data?.generationMode === 'fallback') {
        notify('Course generated in safe draft mode. Click Regenerate With AI for richer content.', 'warning');
      } else {
        notify('Personal course generated', 'success');
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        setUpgradeRequired(true);
      }
      notify(error?.response?.data?.output || 'Failed to generate personal course', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const openCourse = (selectedCourse) => {
    setCourse(selectedCourse);
    setStep(3);
    setExpandedModuleId(null);
  };

  const deleteCourse = async (courseId) => {
    const shouldDelete = window.confirm('Delete this course permanently?');
    if (!shouldDelete) return;

    try {
      const response = await axios.delete(`${ENDPOINTS.DELETE_PERSONAL_COURSE}${courseId}`, {
        headers: { Authorization: `Bearer ${currentUser.token}` }
      });

      if (!response?.data?.success) {
        notify(response?.data?.output || 'Failed to delete course', 'error');
        return;
      }

      const remainingCourses = courses.filter((item) => item.id !== courseId);
      setCourses(remainingCourses);
      if (course?.id === courseId) {
        if (remainingCourses.length > 0) {
          setCourse(remainingCourses[0]);
          setStep(3);
        } else {
          setCourse(null);
          setStep(1);
        }
      }
      notify('Course deleted successfully', 'success');
    } catch (error) {
      notify(error?.response?.data?.output || 'Failed to delete course', 'error');
    }
  };

  const regenerateCourse = async () => {
    if (!course?.id) return;
    setIsRegenerating(true);
    try {
      const response = await axios.post(
        `${ENDPOINTS.REGENERATE_PERSONAL_COURSE}${course.id}/regenerate`,
        { goal, language: apiLanguageByUi[language] || 'English' },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      if (!response?.data?.success) {
        notify(response?.data?.output || 'Failed to regenerate course', 'error');
        return;
      }

      const updatedCourse = response.data.data.course;
      setCourse(updatedCourse);
      setCourses((previous) => previous.map((item) => (item.id === updatedCourse.id ? updatedCourse : item)));
      if (response?.data?.data?.generationMode === 'fallback') {
        notify('Regeneration completed with fallback draft mode.', 'warning');
      } else {
        notify('Course regenerated with AI', 'success');
      }
    } catch (error) {
      notify(error?.response?.data?.output || 'Failed to regenerate course', 'error');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: '14px', md: '20px' }} bg={pageBg} minH="100vh">
      <Box
        borderRadius="24px"
        p={{ base: '18px', md: '28px' }}
        mb="20px"
        color="white"
        bg={heroBg}
        boxShadow="xl"
      >
        <Text textTransform="uppercase" fontSize="xs" letterSpacing="1.2px" opacity={0.88}>
          Learn From Anything
        </Text>
        <Heading mt="2" fontSize={{ base: '2xl', md: '4xl' }}>Create Personal Course</Heading>
        <Text mt="3" maxW="760px" opacity={0.92}>
          Generate a 7-day personalized micro-course from a topic or pasted text, then learn with guided lessons and practice loops.
        </Text>
        <HStack spacing="8px" mt="4" wrap="wrap">
          <Badge bg="whiteAlpha.300" color="white" px="10px" py="4px" borderRadius="full">Premium</Badge>
          <Badge bg="whiteAlpha.300" color="white" px="10px" py="4px" borderRadius="full">Adaptive</Badge>
          <Badge bg="whiteAlpha.300" color="white" px="10px" py="4px" borderRadius="full">Spaced Review</Badge>
        </HStack>
        <Button mt="4" size="sm" bg="whiteAlpha.300" _hover={{ bg: 'whiteAlpha.400' }} color="white" onClick={startNewCourse}>
          New Course
        </Button>
      </Box>

      {upgradeRequired && (
        <Box bg={premiumCardBg} border="1px solid" borderColor={premiumBorder} borderRadius="20px" p="20px" boxShadow="md" mb="20px">
          <Heading fontSize="2xl">Upgrade Required</Heading>
          <Text mt="2" color={mutedText}>This feature is available only on the Premium plan.</Text>
          <Button mt="4" variant="darkBrand" onClick={() => navigate('/admin/profile')}>View Plans</Button>
        </Box>
      )}

      {!upgradeRequired && (
        <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor} mb="20px">
          <HStack justify="space-between" align="center" mb="12px">
            <Heading fontSize="xl">Generated Courses</Heading>
            <HStack>
              <Button size="sm" variant="darkBrand" onClick={startNewCourse}>New Course</Button>
              <Button size="sm" variant="outline" onClick={fetchCourses} isLoading={loadingCourses}>Refresh</Button>
            </HStack>
          </HStack>
          {courses.length === 0 ? (
            <Text color={mutedText}>No personal courses yet. Generate your first one below.</Text>
          ) : (
            <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="10px">
              {courses.map((item) => (
                <Box key={item.id} p="12px" borderRadius="12px" bg={accentBg} border="1px solid" borderColor={borderColor}>
                  <HStack justify="space-between" align="start">
                    <Box>
                      <Text fontWeight="700">{item.title}</Text>
                      <Text fontSize="sm" color={mutedText}>
                        {item.moduleCount || 0} modules · {item.lessonCount || 0} lessons · {item.completedLessons || 0} done · {item.status}
                      </Text>
                      <Text fontSize="xs" color={mutedText}>
                        Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}
                      </Text>
                    </Box>
                    <HStack>
                      <Button size="xs" variant="darkBrand" onClick={() => openCourse(item)}>Open</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => deleteCourse(item.id)}>Delete</Button>
                    </HStack>
                  </HStack>
                </Box>
              ))}
            </SimpleGrid>
          )}
        </Box>
      )}

      {!upgradeRequired && step <= 2 && (
        <Grid templateColumns={{ base: '1fr', xl: '1.2fr 0.8fr' }} gap="20px">
          <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor}>
            <HStack justify="space-between" mb="14px" wrap="wrap">
              <Text fontWeight="700">Step {step} of 2</Text>
              <HStack spacing="8px">
                <Badge colorScheme={step === 1 ? 'blue' : 'gray'}>Source</Badge>
                <Badge colorScheme={step === 2 ? 'blue' : 'gray'}>Plan Setup</Badge>
              </HStack>
            </HStack>

            <Divider mb="14px" />

            {step === 1 && (
              <Stack spacing="12px">
                <Text fontWeight="700">Choose Source</Text>
                <RadioGroup value={sourceType} onChange={setSourceType}>
                  <HStack spacing="20px">
                    <Radio value="topic">Topic</Radio>
                    <Radio value="paste">Paste Text</Radio>
                  </HStack>
                </RadioGroup>

                {sourceType === 'topic' ? (
                  <Input
                    size="lg"
                    value={topic}
                    onChange={(event) => setTopic(event.target.value)}
                    placeholder="e.g. Adaptive Leadership"
                    bg={accentBg}
                  />
                ) : (
                  <Textarea
                    value={sourceText}
                    onChange={(event) => setSourceText(event.target.value)}
                    placeholder="Paste notes/article/transcript text..."
                    minH="190px"
                    bg={accentBg}
                  />
                )}

                <Button alignSelf="flex-start" variant="darkBrand" onClick={() => setStep(2)}>Continue</Button>
              </Stack>
            )}

            {step === 2 && (
              <Stack spacing="14px">
                <Text fontWeight="700">Goal</Text>
                <HStack spacing="8px" wrap="wrap">
                  {goals.map((item) => (
                    <Button key={item} size="sm" variant={goal === item ? 'darkBrand' : 'outline'} onClick={() => setGoal(item)}>{item}</Button>
                  ))}
                </HStack>

                <Text fontWeight="700">Starting Level</Text>
                <HStack spacing="8px" wrap="wrap">
                  {levels.map((item) => (
                    <Button key={item} size="sm" variant={level === item ? 'darkBrand' : 'outline'} onClick={() => setLevel(item)}>
                      {item.charAt(0).toUpperCase() + item.slice(1)}
                    </Button>
                  ))}
                </HStack>

                <Text fontWeight="700">Minutes Per Day</Text>
                <Select value={minutesPerDay} onChange={(event) => setMinutesPerDay(Number(event.target.value))} bg={accentBg}>
                  <option value={10}>10 minutes</option>
                  <option value={20}>20 minutes</option>
                  <option value={30}>30 minutes</option>
                </Select>

                <HStack>
                  <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                  <Button variant="darkBrand" isLoading={isGenerating} onClick={createCourse}>Generate Course</Button>
                </HStack>
              </Stack>
            )}
          </Box>

          <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor}>
            <Heading fontSize="xl">What You’ll Get</Heading>
            <List mt="12px" spacing="10px">
              <ListItem color={mutedText}>4-8 modules tailored to your goal and level.</ListItem>
              <ListItem color={mutedText}>1-3 lessons per module with key points.</ListItem>
              <ListItem color={mutedText}>Practice drills, quizzes, and flashcards.</ListItem>
              <ListItem color={mutedText}>Spaced review queue generated automatically.</ListItem>
              <ListItem color={mutedText}>Progress and streak hooks integrated with Junus rewards.</ListItem>
            </List>
          </Box>
        </Grid>
      )}

      {step === 3 && course && (
        <Grid templateColumns={{ base: '1fr', xl: '1.1fr 1fr' }} gap="20px" pb="20px">
          <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor}>
            <HStack justify="space-between" align="start" wrap="wrap">
              <Heading fontSize={{ base: 'xl', md: '2xl' }}>{course.title}</Heading>
              <HStack>
                <Badge colorScheme={currentGenerationMode === 'fallback' ? 'orange' : 'green'}>
                  {currentGenerationMode === 'fallback' ? 'Draft Mode' : 'AI Mode'}
                </Badge>
                <Button size="sm" variant="outline" onClick={regenerateCourse} isLoading={isRegenerating}>
                  Regenerate With AI
                </Button>
              </HStack>
            </HStack>
            <Text mt="2" color={mutedText}>{course.coursePack?.summary}</Text>
            <Text mt="2" fontSize="sm" color={mutedText}>
              How to use: start Today&apos;s Plan lesson, read Learning Content, complete one practice item, then click Next.
            </Text>
            <HStack justify="space-between" mt="4">
              <Text fontSize="sm" color={mutedText}>Progress</Text>
              <Text fontSize="sm" fontWeight="700">{progressValue}%</Text>
            </HStack>
            <Progress mt="2" value={progressValue} borderRadius="full" />

            <Text mt="5" fontWeight="700">Modules</Text>
            <Stack mt="3" spacing="10px">
              {modules.map((module) => (
                <Box key={module.id} p="12px" borderRadius="12px" bg={moduleBg} border="1px solid" borderColor={moduleBorder}>
                  <HStack justify="space-between" align="start" wrap="wrap">
                    <Box>
                      <Text fontWeight="700">{module.title}</Text>
                      <Text fontSize="sm" color={mutedText}>{module.objective}</Text>
                      <Badge mt="1">{module.lessons?.length || 0} lessons</Badge>
                    </Box>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => setExpandedModuleId((previous) => (previous === module.id ? null : module.id))}
                    >
                      {expandedModuleId === module.id ? 'Hide Lessons' : 'View Lessons'}
                    </Button>
                  </HStack>

                  {expandedModuleId === module.id && (
                    <SimpleGrid columns={1} spacing="8px" mt="10px">
                      {(module.lessons || []).map((lesson) => (
                        <Box
                          key={lesson.id}
                          p="10px"
                          borderRadius="10px"
                          bg={accentBg}
                          border="1px solid"
                          borderColor={borderColor}
                        >
                          <HStack justify="space-between" align="center" wrap="wrap">
                            <Box>
                              <Text fontWeight="700">{lesson.title}</Text>
                              <Text fontSize="xs" color={mutedText}>Open this lesson directly</Text>
                            </Box>
                            <Button
                              size="xs"
                              variant="darkBrand"
                              onClick={() => navigate(`/admin/personal-courses/${course.id}/lesson/${module.id}/${lesson.id}`)}
                            >
                              Start
                            </Button>
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>

          <Box bg={cardBg} borderRadius="20px" p="20px" boxShadow="md" border="1px solid" borderColor={borderColor}>
            <Heading fontSize="xl">Today's Plan</Heading>
            <Text mt="1" color={mutedText}>{todaysPlanItems.length} planned today · {totalLessons} lessons in full course</Text>

            <SimpleGrid columns={1} spacing="10px" mt="4">
              {todaysPlanItems.map((item) => (
                <Box key={`${item.moduleId}-${item.lessonId}`} p="12px" borderRadius="12px" bg={accentBg} border="1px solid" borderColor={borderColor}>
                  <Text fontWeight="700">{item.title}</Text>
                  <Text fontSize="sm" color={mutedText}>Focused practice block</Text>
                  <Button mt="2" size="sm" variant="darkBrand" onClick={() => navigate(`/admin/personal-courses/${course.id}/lesson/${item.moduleId}/${item.lessonId}`)}>
                    Start
                  </Button>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Grid>
      )}
    </Box>
  );
}
